// Privacy.js
import React from 'react';
import '../css/Privacy.css';

const Privacy = () => {
  return (
    <div className="privacy-container">
        <h1>Privacy Policy</h1>
        <p className="last-updated"><strong>Last Updated:</strong> February 25, 2026</p>

        <section>
          <h2>What LinknTrack Does</h2>
          <p>
            LinknTrack is a Chrome Extension that lets you add personal notes to LinkedIn profiles. You write notes, we store them. That's the core of it.
          </p>
        </section>

        <section>
          <h2>What We Collect</h2>

          <h3>When You Sign Up</h3>
          <p>
            We collect your first name, last name, email address, and password. Your password is hashed before we store it. We never see or store your actual password.
          </p>
          <p>
            If you sign up with Google, we receive your name, email, and Google account ID from Google's OAuth service. We don't get your Google password.
          </p>

          <h3>When You Use the Extension</h3>
          <p>
            We collect the notes you write: titles, content, tags, colors, and the LinkedIn username of the profile you attach them to. Note content is encrypted before it hits our database.
          </p>
          <p>
            The extension reads the LinkedIn username from the page URL (e.g., <code>linkedin.com/in/mariemehab</code>). It does not read or scrape anything else from the LinkedIn page. No profile info, no messages, no feed content.
          </p>
          <p>
            The extension stores two things locally in your browser: your display preference (fixed or embedded panel) and the panel's position/collapse state. That's it.
          </p>

          <h3>When You Use the Contact Form</h3>
          <p>
            We collect your first name, last name, email, the purpose you select, and your message. This gets stored in our database and sent to our team.
          </p>

          <h3>Google User Data (Gmail API)</h3>
          <p>
            We use a single Gmail API scope: <code>gmail.send</code>. This lets our server send emails from our application email address. We use it for three things:
          </p>
          <ul>
            <li><strong>Note reminders:</strong> when you set a reminder on a note, we email you at the scheduled time.</li>
            <li><strong>Email verification:</strong> a one-time code to confirm your email during signup.</li>
            <li><strong>Password reset:</strong> a reset link when you forget your password.</li>
          </ul>
          <p>
            We do not read your inbox. We do not access your drafts, labels, contacts, or any other Gmail data. We only send outbound emails. Nothing else.
          </p>
        </section>

        <section>
          <h2>Analytics</h2>
          <p>
            We use Google Analytics on our website to understand traffic: which pages get visited, how long people stay, what devices they use. Google Analytics may set its own cookies on your browser. You can opt out of Google Analytics by installing Google's <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">opt-out browser add-on</a>.
          </p>
          <p>
            We also load fonts from Google's servers, which means Google receives your IP address when the font loads.
          </p>
        </section>

        <section>
          <h2>Cookies</h2>
          <p>
            We set one cookie for your login session. Here's what's true about it:
          </p>
          <ul>
            <li>It's <strong>HttpOnly</strong>, meaning JavaScript on the page can't read it.</li>
            <li>It's <strong>Secure</strong> in production, meaning it's only sent over HTTPS.</li>
            <li>It expires after <strong>24 hours</strong>.</li>
          </ul>
          <p>
            Google Analytics may set additional cookies. We don't control those. See <a href="https://policies.google.com/technologies/cookies" target="_blank" rel="noopener noreferrer">Google's cookie policy</a> for details.
          </p>
        </section>

        <section>
          <h2>How We Use Your Data</h2>
          <ul>
            <li>Your account info is used to log you in and identify your notes.</li>
            <li>Your notes are stored so you can view, edit, and delete them across sessions.</li>
            <li>The Gmail API is used only to send you the three types of emails listed above.</li>
            <li>Contact form submissions are used to respond to your inquiries.</li>
            <li>Analytics data is used to understand how people use the website.</li>
          </ul>
          <p>
            We don't use your data for advertising. We don't use it for market research. We don't use it to train AI or machine learning models.
          </p>
        </section>

        <section>
          <h2>Who Else Gets Your Data</h2>
          <p>
            We don't sell, rent, or hand over your data to anyone. The only third parties involved are:
          </p>
          <ul>
            <li><strong>Google:</strong> Gmail API for sending emails, Google Analytics for website traffic, Google Fonts for typography, Google OAuth for sign-in.</li>
          </ul>
          <p>
            If law enforcement shows up with a valid legal order, we'll comply. Outside of that, your data stays with us.
          </p>
        </section>

        <section>
          <h2>How We Protect Your Data</h2>
          <ul>
            <li>Note content is encrypted before it's stored in our database.</li>
            <li>Passwords are hashed. We never store them in plain text.</li>
            <li>Verification codes and password reset tokens are hashed before storage.</li>
            <li>All traffic between your browser and our server runs over HTTPS.</li>
            <li>Login sessions use HttpOnly cookies that page scripts can't touch.</li>
            <li>Our API has rate limiting to prevent abuse.</li>
            <li>We use security headers and restrict which origins can communicate with our server.</li>
            <li>All incoming data is validated and unknown fields are rejected before anything gets processed.</li>
          </ul>
        </section>

        <section>
          <h2>How Long We Keep Your Data</h2>
          <ul>
            <li><strong>Account and notes:</strong> kept as long as your account exists.</li>
            <li><strong>Verification codes:</strong> expire after 24 hours, then cleared.</li>
            <li><strong>Password reset tokens:</strong> expire after 1 hour, then cleared.</li>
            <li><strong>Contact form messages:</strong> kept until we delete them.</li>
            <li><strong>Gmail data:</strong> we don't store any. Emails are sent and forgotten.</li>
          </ul>
        </section>

        <section>
          <h2>Deleting Your Data</h2>
          <p>
            You can delete individual notes yourself through the extension or the dashboard. To delete your entire account and all associated data, email us at <strong>linkntrack@gmail.com</strong>. We'll handle it within 30 days.
          </p>
          <p>
            You can revoke LinknTrack's access to your Google account anytime at <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">myaccount.google.com/permissions</a>.
          </p>
        </section>

        <section>
          <h2>Chrome Extension Permissions</h2>
          <h3>Storage</h3>
          <p>
            Used to save your panel position and display preference locally in the browser. Nothing sensitive.
          </p>
          <h3>Scripting</h3>
          <p>
            Used to inject the notes panel into LinkedIn profile pages. The extension needs to run JavaScript on LinkedIn to add its UI to the page.
          </p>
        </section>

        <section>
          <h2>Changes to This Policy</h2>
          <p>
            If we change this policy, we'll update the "Last Updated" date at the top. Check back here if you want to stay current.
          </p>
        </section>

        <section>
          <h2>Contact</h2>
          <p>
            Questions about your data? Want something deleted? Email us at <strong>linkntrack@gmail.com</strong>.
          </p>
        </section>
    </div>
  );
};

export default Privacy;
